/**
 * Tiny presentation helpers, local to the issue/board/backlog features.
 *
 * These are deliberately self-contained rather than imported from `@/lib`: the
 * three feature directories are built in parallel with the app shell, and a
 * one-line class joiner is not worth a cross-feature build dependency.
 */

export type ClassValue = string | number | null | undefined | false | ClassValue[]

/** Conditional className joiner. */
export function cn(...values: ClassValue[]): string {
  const out: string[] = []
  for (const value of values) {
    if (!value && value !== 0) continue
    if (Array.isArray(value)) {
      const nested = cn(...value)
      if (nested) out.push(nested)
    } else {
      out.push(String(value))
    }
  }
  return out.join(' ')
}

/** `Mohamed Tawfiq` -> `MT`. Falls back to the first character of an email. */
export function initials(name: string | null | undefined, email?: string | null): string {
  const source = (name ?? '').trim() || (email ?? '').trim()
  if (!source) return '?'
  const parts = source.split(/[\s._-]+/).filter(Boolean)
  if (parts.length === 0) return source.slice(0, 1).toUpperCase()
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 7],
  ['week', 4.348],
  ['month', 12],
  ['year', Number.POSITIVE_INFINITY],
]

/**
 * `2 hours ago`. Every API timestamp is ISO-8601 with six fractional digits and
 * a literal Z, which `new Date()` parses natively — nothing here reformats a
 * value that is going back to the server.
 */
export function relativeTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'

  let delta = (date.getTime() - Date.now()) / 1000
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  for (const [unit, step] of RELATIVE_UNITS) {
    if (Math.abs(delta) < step || unit === 'year') {
      return formatter.format(Math.round(delta), unit)
    }
    delta /= step
  }
  return formatter.format(Math.round(delta), 'year')
}

/** `13 Feb 2026`, for both timestamps and bare `yyyy-mm-dd` due dates. */
export function formatDay(value: string | null | undefined): string {
  if (!value) return '—'
  // A bare date must not be shifted into the previous day by the local zone.
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00Z` : value
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

/** `13 Feb 2026, 10:30`. */
export function formatMoment(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

/** Today's date as `yyyy-mm-dd` in the viewer's zone — the due-date cutoff. */
export function todayISO(): string {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
}

/** A due date is late when it is strictly before today and the issue is open. */
export function isOverdue(dueDate: string | null | undefined, done: boolean): boolean {
  if (!dueDate || done) return false
  return dueDate.slice(0, 10) < todayISO()
}

/** `1 issue` / `4 issues`. */
export function pluralize(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`
}

/** Story points render without a trailing `.0`. */
export function formatPoints(points: number | null | undefined): string {
  if (points === null || points === undefined) return ''
  return Number.isInteger(points) ? String(points) : points.toFixed(1)
}
